"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isValidTaxIdentifierNumber = void 0;
const constants_1 = require("../constants");
/**
 * Per-type tax-identifier number validators, taken verbatim from the FATCA reporting wiki. Pure
 * functions (no I/O) so they are deterministically unit-testable and shared across every layer — the
 * API save path (authoritative), the CSV importer, and the web forms (live "as-you-type" feedback).
 *
 * Australian types carry a checksum; US types carry a format plus band/prefix rules; GIIN is a
 * 4-segment structure. Foreign/Other types are unvalidated free text. "Not provided" permits no
 * number; an unset type is not flagged.
 */
const digitsOnly = (value) => value.replace(/\D/g, "");
/** ATO TFN checksum: weighted sum mod 11 over the 8- or 9-digit number. */
const isValidTfn = (value) => {
    const digits = digitsOnly(value);
    if (digits.length !== 8 && digits.length !== 9)
        return false;
    const weights = [1, 4, 3, 7, 5, 8, 6, 9, 10];
    const sum = digits.split("").reduce((acc, digit, index) => acc + Number(digit) * weights[index], 0);
    return sum % 11 === 0;
};
/** ABN checksum: subtract 1 from the first digit, weighted sum mod 89. */
const isValidAbn = (value) => {
    const digits = digitsOnly(value);
    if (digits.length !== 11)
        return false;
    const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
    const sum = digits
        .split("")
        .reduce((acc, digit, index) => acc + (index === 0 ? Number(digit) - 1 : Number(digit)) * weights[index], 0);
    return sum % 89 === 0;
};
/** ACN checksum: weighted sum mod 10 complement over the first 8 digits equals the 9th. */
const isValidAcn = (value) => {
    const digits = digitsOnly(value);
    if (digits.length !== 9)
        return false;
    const weights = [8, 7, 6, 5, 4, 3, 2, 1];
    const sum = weights.reduce((acc, weight, index) => acc + Number(digits[index]) * weight, 0);
    const complement = (10 - (sum % 10)) % 10;
    return complement === Number(digits[8]);
};
/** US SSN: `AAA-GG-SSSS` (3-2-4), digits only, no checksum. */
const isValidSsn = (value) => /^\d{3}-\d{2}-\d{4}$/.test(value);
const inRange = (value, low, high) => value >= low && value <= high;
/** US ITIN: begins with 9; middle two digits (pos 4–5) in the IRS-assigned bands. */
const isValidItin = (value) => {
    const digits = digitsOnly(value);
    if (digits.length !== 9 || digits[0] !== "9")
        return false;
    const middle = Number(digits.slice(3, 5));
    return inRange(middle, 50, 65) || inRange(middle, 70, 88) || inRange(middle, 90, 92) || inRange(middle, 94, 99);
};
const EIN_INVALID_PREFIXES = new Set([
    "00",
    "07",
    "08",
    "09",
    "17",
    "18",
    "19",
    "28",
    "29",
    "49",
    "69",
    "70",
    "78",
    "79",
    "89",
    "96",
    "97",
]);
/** US EIN: `XX-XXXXXXX`; the two-digit prefix must not be in the IRS invalid list. */
const isValidEin = (value) => {
    if (!/^\d{2}-\d{7}$/.test(value))
        return false;
    return !EIN_INVALID_PREFIXES.has(value.slice(0, 2));
};
/** FATCA GIIN: `XXXXXX.XXXXX.XX.XXX` — 6-char FATCA ID / 5-char entity / 2-char category / 3-digit country. */
const isValidGiin = (value) => /^[0-9A-Z]{6}\.[0-9A-Z]{5}\.[A-Z]{2}\.\d{3}$/.test(value);
/**
 * Validate a tax-identifier number against its type's rule. Types without a format rule
 * (Foreign/Other) accept any value; "Not provided" permits no number; an unset type is not flagged.
 */
const isValidTaxIdentifierNumber = (type, value) => {
    const number = (value ?? "").trim();
    switch (type) {
        case constants_1.TaxIdentifierType.AUSTRALIAN_TFN:
            return isValidTfn(number);
        case constants_1.TaxIdentifierType.AUSTRALIAN_ABN:
            return isValidAbn(number);
        case constants_1.TaxIdentifierType.AUSTRALIAN_ACN:
            return isValidAcn(number);
        case constants_1.TaxIdentifierType.US_SSN:
            return isValidSsn(number);
        case constants_1.TaxIdentifierType.US_ITIN:
            return isValidItin(number);
        case constants_1.TaxIdentifierType.US_EIN:
            return isValidEin(number);
        case constants_1.TaxIdentifierType.FATCA_GIIN:
            return isValidGiin(number);
        case constants_1.TaxIdentifierType.NOT_PROVIDED:
            // No number permitted; the reason/note requirement is enforced by the structural rules elsewhere.
            return number.length === 0;
        default:
            // Foreign/Other free-text types and an unselected type are not format-checked.
            return true;
    }
};
exports.isValidTaxIdentifierNumber = isValidTaxIdentifierNumber;
//# sourceMappingURL=taxIdentifierValidation.js.map